import { USER_PLAN_QUERY_KEY } from "@/components/Root/hooks/useUserPlanData";
import { TUserPlanData } from "@/types/plan-data";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useAuth } from "reactfire";
import { CHECKOUT_EVENTS } from "../../events.checkout";
import {
  AddOnRemovePayload,
  handleAddOnRemove,
} from "../../handlers/handleAddOnRemove";
import { CheckoutService } from "../../service.checkout";

export function useAddOnRemoveMutation() {
  const auth = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: AddOnRemovePayload) => {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("No authentication token");

      const result = await handleAddOnRemove({
        token,
        payload,
      });

      if (!result.status) {
        throw new Error("Failed to remove add-on");
      }

      return result;
    },
    onMutate: async (payload) => {
      await qc.cancelQueries({ queryKey: [USER_PLAN_QUERY_KEY] });

      const previousPlanData = qc.getQueryData<TUserPlanData>([
        USER_PLAN_QUERY_KEY,
      ]);

      if (previousPlanData) {
        const { product_id, final_quantity = 0 } = payload;

        const updatedUpsells =
          final_quantity === 0
            ? previousPlanData.purchased_upsells.filter(
                (addon) => addon.product_ref_id !== product_id,
              )
            : previousPlanData.purchased_upsells.map((addon) =>
                addon.product_ref_id === product_id
                  ? { ...addon, quantity: final_quantity }
                  : addon,
              );

        qc.setQueryData<TUserPlanData>([USER_PLAN_QUERY_KEY], {
          ...previousPlanData,
          purchased_upsells: updatedUpsells,
        });
      }

      return { previousPlanData };
    },
    onSuccess: () => {
      CheckoutService.track(CHECKOUT_EVENTS.ADDON_REMOVE_SUCCESS, {
        types: ["posthog", "customerio"],
      });
      toast.success("Add-on removed successfully");
    },
    onError: (error, _variables, context) => {
      if (context?.previousPlanData) {
        qc.setQueryData([USER_PLAN_QUERY_KEY], context.previousPlanData);
      }
      CheckoutService.track(CHECKOUT_EVENTS.ADDON_REMOVE_FAILED, {
        types: ["posthog", "customerio"],
        properties: {
          error: error instanceof Error ? error.message : "Unknown error",
        },
      });
      console.error("Error removing add-on:", error);
      toast.error("Failed to remove add-on. Please try again.");
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: [USER_PLAN_QUERY_KEY] });
    },
  });
}
